// API client for Assessment coupons (/api/assessment/admin/coupons).
//
// Envelope: { data, isError, statusCode, errorMessage }. A coupon belongs to
// exactly one Course Category OR exactly one Test Series. The list is a page
// { content, page, size, totalElements, totalPages, hasNext }, newest first, and every filter is optional:
//   GET    /coupons?page&size&code&targetType&categoryId|seriesId&active
//          (code: case-insensitive "contains"; targetType: CATEGORY | SERIES; categoryId and seriesId
//           together are rejected). Each item carries targetType and targetName.
//   POST   /coupons              create: code, discountType, discountValue and exactly one of
//                                categoryId / seriesId required
//   PUT    /coupons/{id}         partial: only fields that are sent change - a field cannot be
//                                cleared back to null once set
//   DELETE /coupons/{id}         soft: sets active=false (the row stays listed)
// Student purchase looks the code up case-insensitively among ACTIVE coupons, and `code`
// is globally unique (DB constraint), so a duplicate code surfaces as a backend error.
import api from "../axiosInstance";

export { apiErrorMessage } from "./assessmentHierarchy";

const BASE = "/api/assessment/admin/coupons";

const unwrap = (res) => res.data?.data;

export const DISCOUNT_TYPES = [
  { value: "PERCENTAGE", label: "Percentage (%)" },
  { value: "FLAT", label: "Flat amount (₹)" },
];

export const normalizeCoupon = (c) => ({
  ...c,
  status: c.active ? "ACTIVE" : "INACTIVE",
  usageCount: c.usageCount ?? 0,
});

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

// Returns the page object with each coupon normalised. All filters are optional.
export const listCoupons = async ({
  page = 0,
  size = 25,
  code,
  targetType,
  categoryId,
  seriesId,
  active,
  discountType,
} = {}) => {
  if (categoryId != null && seriesId != null) {
    throw new Error("listCoupons accepts at most one of categoryId or seriesId");
  }
  const res = await api.get(BASE, {
    params: cleanParams({ page, size, code, targetType, categoryId, seriesId, active, discountType }),
  });
  const data = unwrap(res) || {};
  return { ...data, content: (data.content || []).map(normalizeCoupon) };
};

// Payload: code, categoryId or seriesId (create only), discountType, discountValue,
// maxDiscountAmount, usageLimit, perUserUsageLimit, startDate, endDate (ISO
// date-time with offset), active.
export const createCoupon = (payload) => api.post(BASE, payload).then(unwrap);
export const updateCoupon = (id, payload) => api.put(`${BASE}/${id}`, payload).then(unwrap);
export const deactivateCoupon = (id) => api.delete(`${BASE}/${id}`).then(unwrap);
